import { CASE_FLAGS, CASE_STATUSES, type CaseFlag, type CaseListItemDto, type CaseStatus, datePl, formatDuration, type MeDto, type NextActionDto, type NextActionKind, type NextActionLinkDto, type Page } from '@bqcrm/shared';
import { Badge, Button, Card, Collapse, Group, MultiSelect, Pagination, Select, Stack, Switch, Table, Text, TextInput } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { IconAdjustmentsHorizontal, IconBriefcase, IconPlus, IconSearch, IconUser, IconUsersGroup } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api } from '../api';
import { can, canQuickCase, useMe } from '../auth';
import { CaseFlagBadges, CaseStatusBadge, CompanyBadgeById, PriorityBadge, TradeBadge } from '../components/Badges';
import { PageHeader } from '../components/PageHeader';
import { openQuickCase } from '../quickCase';
import { QueryState } from '../components/QueryState';
import { EmptyState, FilterChips } from '../components/ui';
import { useCompanies, useOfficeUsers, useTrades } from '../data';
import { formatDateTime } from '../format';

const LIST_REFRESH_MS = 60_000;

const PAGE = 50;
// status główny procesu (docs/10 §3.8) — aktywne i zamknięte
const OPEN_STAGES: CaseStatus[] = ['nowa', 'weryfikacja', 'oferta', 'umowa', 'realizacja', 'weryfikacja_koncowa'];
const CLOSED_STAGES: CaseStatus[] = ['zamknieta', 'odrzucona', 'przegrana', 'anulowana'];
const ALL_STAGES = Object.keys(CASE_STATUSES) as CaseStatus[];
type View = 'aktywne' | 'zamkniete' | 'wszystkie';
const VIEW_STAGES: Record<View, CaseStatus[]> = { aktywne: OPEN_STAGES, zamkniete: CLOSED_STAGES, wszystkie: ALL_STAGES };

const TONE: Record<NextActionDto['tone'], { color: string; fw: number }> = {
  // zmienne motywu: w trybie ciemnym jaśniejszy odcień (eko-8 / red-7 na ciemnym tle były słabo czytelne)
  late: { color: 'var(--app-text-red)', fw: 600 },
  todo: { color: 'var(--app-text-eko)', fw: 600 },
  wait: { color: 'dimmed', fw: 500 },
  ok: { color: 'green.7', fw: 500 },
};

/** Uprawnienie potrzebne, żeby przejście z podpowiedzi miało sens (interfejs tylko ukrywa — decyduje serwer). */
const ACTION_PERMISSION: Record<NextActionKind, string> = {
  nowa_oferta: 'oferty.edycja',
  oferta: 'oferty.podglad',
  nowa_umowa: 'umowy.edycja',
  umowa: 'umowy.podglad',
  planowanie: 'planowanie.edycja',
  realizacja: 'sprawy.podglad',
  zadania: 'sprawy.podglad',
  prace: 'sprawy.podglad',
  praca: 'sprawy.podglad',
  zadanie:'planowanie.edycja',
  zakoncz: 'sprawy.edycja',
  finanse: 'finanse.podglad',
  zamknij_przegrana: 'sprawy.edycja',
};

/** Adres funkcji, do której prowadzi podpowiedź — budowany tylko z rodzaju i identyfikatora (bez adresów z danych). */
export function nextActionHref(a: NextActionLinkDto, c: { id: string; number: string }): string {
  const id = encodeURIComponent(a.id ?? '');
  switch (a.kind) {
    case 'nowa_oferta':
      return `/sprawy/${c.id}?zakladka=oferty&nowa-oferta=${id}`;
    case 'oferta':
      return `/oferty/${id}`;
    case 'nowa_umowa':
      return `/sprawy/${c.id}?zakladka=umowy&nowa-umowa=${id}`;
    case 'umowa':
      return `/umowy/${id}`;
    case 'planowanie':
      return a.id ? `/planowanie?zaplanuj=${id}` : `/planowanie?szukaj=${encodeURIComponent(c.number)}`;
    case 'realizacja':
      return `/sprawy/${c.id}?realizacja=1`;
    case 'zadania':
      return `/sprawy/${c.id}?zakladka=zadania`;
    case 'prace':
      return `/sprawy/${c.id}?zakladka=prace`;
    // jedna praca w sprawie — otwarty panel zadania (np. uwagi klienta w jej protokole)
    case 'praca':
      return `/sprawy/${c.id}?zakladka=prace&zadanie=${id}`;
    case 'zadanie':
      return `/planowanie?zadanie=${id}`;
    case 'zakoncz':
      return `/sprawy/${c.id}?zakonczenie=1`;
    case 'finanse':
      return `/finanse?widok=${id || 'faktury'}&sprawa=${encodeURIComponent(c.number)}`;
    case 'zamknij_przegrana':
      return `/sprawy/${c.id}?zamkniecie=przegrana`;
  }
}

/** Przejście z podpowiedzi, jeśli jest i użytkownik ma do niego dostęp. */
export function useNextActionHref(next: NextActionDto | null | undefined, c: { id: string; number: string } | undefined): string | null {
  const me = useMe();
  const a = next?.action;
  // krok wymagający pracy (np. wydanie oferty) — tylko z uprawnieniem do niej; inaczej z uprawnieniem podglądu
  if (!a || !c || !can(me.data, a.need ?? ACTION_PERMISSION[a.kind])) return null;
  return nextActionHref(a, c);
}

/**
 * „Następny krok”: wpisany ręcznie albo wyliczony ze stanu sprawy (zaległe — na czerwono).
 * Z `c` podpowiedź z akcją jest linkiem do funkcji (oferta, umowa, planowanie); `nested` — wewnątrz innego linku (karta na telefonie).
 */
export function NextAction({ next, size = 'sm', c, nested = false }: { next: NextActionDto | null | undefined; size?: 'xs' | 'sm'; c?: { id: string; number: string }; nested?: boolean }) {
  const href = useNextActionHref(next, c);
  const navigate = useNavigate();
  if (!next) return <Text span c="dimmed" size={size}>—</Text>;
  const t = TONE[next.tone];
  const label = (
    <>
      <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>{next.text}</span>
      <span style={{ whiteSpace: 'nowrap' }}> ›</span>
    </>
  );
  return (
    <div>
      {href ? (
        nested ? (
          <Text
            size={size}
            c={t.color}
            fw={t.fw}
            lineClamp={2}
            role="link"
            tabIndex={0}
            style={{ cursor: 'pointer' }}
            title={next.action!.label}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void navigate(href);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              e.stopPropagation();
              void navigate(href);
            }}
          >
            {label}
          </Text>
        ) : (
          <Text component={Link} to={href} size={size} c={t.color} fw={t.fw} lineClamp={2} title={next.action!.label} style={{ textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>
            {label}
          </Text>
        )
      ) : (
        <Text size={size} c={t.color} fw={t.fw} lineClamp={2}>
          {next.text}
        </Text>
      )}
      {!next.auto && next.date && (
        <Text size="xs" c={next.tone === 'late' ? 'red.7' : 'dimmed'}>
          termin {datePl(next.date)}
        </Text>
      )}
    </div>
  );
}

/** P11: „Duplikat → S/…” z przejściem do sprawy głównej (`nested` — wewnątrz innego linku, karta na telefonie). */
function DuplicateOf({ c, nested = false }: { c: CaseListItemDto; nested?: boolean }) {
  const navigate = useNavigate();
  const d = c.duplicateOf;
  if (!d) return null;
  const to = `/sprawy/${d.id}`;
  return (
    <Text size="xs" c="dimmed" mt={2}>
      Duplikat →{' '}
      {nested ? (
        <Text
          span
          size="xs"
          c="var(--app-link)"
          role="link"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void navigate(to);
          }}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            e.stopPropagation();
            void navigate(to);
          }}
        >
          {d.number}
        </Text>
      ) : (
        <Text component={Link} to={to} span size="xs" c="var(--app-link)" onClick={(e) => e.stopPropagation()}>
          {d.number}
        </Text>
      )}
    </Text>
  );
}

/** Status sprawy (etap) i od kiedy w nim jest. */
function CaseStage({ c }: { c: CaseListItemDto }) {
  return (
    <>
      <Group gap={4} wrap="nowrap">
        <CaseStatusBadge status={c.status} />
        <PriorityBadge priority={c.priority} />
      </Group>
      <DuplicateOf c={c} />
      <Text size="xs" c="dimmed" mt={2}>
        w etapie {formatDuration(Date.now() - Date.parse(c.statusSince))}
      </Text>
    </>
  );
}

/**
 * Co teraz i u kogo: otwarte zadania sprawy — każde w osobnej linii z wykonawcą (albo kolejką) i terminem.
 * Bez otwartych zadań — podpowiedź „Następny krok”.
 */
export function CaseWork({ c, nested = false, max = 3 }: { c: CaseListItemDto; nested?: boolean; max?: number }) {
  const me = useMe();
  if (!c.work.length) return <NextAction next={c.next} c={c} nested={nested} />;
  const shown = c.work.slice(0, max);
  return (
    <Stack gap={6}>
      {shown.map((w, i) => (
        <div key={i} style={{ minWidth: 0 }}>
          <Text size="sm" fw={500} lineClamp={1} c={w.overdue ? 'red.7' : undefined}>
            {w.title}
          </Text>
          <Group gap={6} wrap="wrap">
            {w.assigneeId ? (
              <Badge size="sm" variant="light" color={w.assigneeId === me.data?.id ? 'eko' : 'gray'} leftSection={<IconUser size={11} />} style={{ textTransform: 'none' }}>
                {w.assigneeId === me.data?.id ? 'Ty' : w.assigneeName}
              </Badge>
            ) : w.unstaffed ? (
              <Badge size="sm" variant="light" color="red" style={{ textTransform: 'none' }}>
                nieobsadzone — nikt nie ma funkcji
              </Badge>
            ) : (
              <Badge size="sm" variant="outline" color="orange" leftSection={<IconUsersGroup size={11} />} style={{ textTransform: 'none' }}>
                do przejęcia: {w.queueLabel}
              </Badge>
            )}
            {w.dueAt && (
              <Text size="xs" c={w.overdue ? 'red.7' : 'dimmed'} fw={w.overdue ? 600 : undefined}>
                {w.overdue ? 'po terminie' : 'termin'} {datePl(w.dueAt)}
              </Text>
            )}
          </Group>
        </div>
      ))}
      {c.work.length > max && (
        <Text size="xs" c="dimmed">
          i jeszcze {c.work.length - max}
        </Text>
      )}
    </Stack>
  );
}

const Scopes = ({ c }: { c: CaseListItemDto }) => (
  <Group gap={4}>
    {c.scopes.map((s) => (
      <Group key={s.tradeCode} gap={2} wrap="nowrap" style={{ opacity: s.salesStage === 'przegrany' ? 0.45 : 1 }}>
        <TradeBadge code={s.tradeCode} />
        <CompanyBadgeById id={s.companyId} />
      </Group>
    ))}
  </Group>
);

/**
 * Lista spraw: tabela, gdy miejsca wystarcza, karty — gdy jest wąsko (telefon, tablet, małe okno, wąska kolumna pulpitu).
 * Przełączanie wg szerokości samej listy (zapytanie kontenera w global.css), nie okna — lista bywa w kolumnie 2/3 pulpitu.
 */
export function CaseRows({ items, showClient = true, showOwner = true }: { items: CaseListItemDto[]; showClient?: boolean; showOwner?: boolean }) {
  const navigate = useNavigate();
  return (
    <div className={showClient && showOwner ? 'case-rows' : 'case-rows case-rows--compact'}>
      <Table.ScrollContainer minWidth={showClient && showOwner ? 880 : 740} className="case-rows-table">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Sprawa</Table.Th>
              {showClient && <Table.Th>Klient / lokalizacja</Table.Th>}
              <Table.Th>Zakresy</Table.Th>
              <Table.Th>Etap</Table.Th>
              <Table.Th w={300}>Teraz — u kogo</Table.Th>
              {showOwner && <Table.Th>Opiekun</Table.Th>}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((c) => (
              <Table.Tr key={c.id} className="row-link" onClick={() => navigate(`/sprawy/${c.id}`)}>
                <Table.Td>
                  <Text component={Link} to={`/sprawy/${c.id}`} fw={600} size="sm" c="var(--app-link)" onClick={(e) => e.stopPropagation()}>
                    {c.number}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {c.title}
                  </Text>
                </Table.Td>
                {showClient && (
                  <Table.Td>
                    <Text size="sm" fw={500}>
                      {c.clientName}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {c.siteAddress}
                    </Text>
                  </Table.Td>
                )}
                <Table.Td>
                  <Scopes c={c} />
                </Table.Td>
                <Table.Td style={{ whiteSpace: 'nowrap' }}>
                  <CaseStage c={c} />
                  {c.flags.length > 0 && (
                    <Group gap={4} mt={4} wrap="wrap" style={{ whiteSpace: 'normal', maxWidth: 260 }}>
                      <CaseFlagBadges flags={c.flags.slice(0, 3)} />
                    </Group>
                  )}
                </Table.Td>
                <Table.Td>
                  <CaseWork c={c} />
                </Table.Td>
                {showOwner && (
                  <Table.Td>
                    <Text size="sm">{c.ownerName ?? '—'}</Text>
                    <Text size="xs" c="dimmed">
                      zmieniono {formatDateTime(c.updatedAt)}
                    </Text>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Stack gap={0} className="case-rows-cards">
        {items.map((c) => (
          <Link key={c.id} to={`/sprawy/${c.id}`} className="card-link" style={{ padding: '12px 14px', borderBottom: '1px solid var(--app-border)', display: 'block', color: 'inherit', textDecoration: 'none' }}>
            <Group justify="space-between" wrap="nowrap" mb={4}>
              <Text fw={700} size="sm" c="var(--app-link)">
                {c.number}
              </Text>
              <Group gap={4} wrap="nowrap">
                <PriorityBadge priority={c.priority} />
                <CaseStatusBadge status={c.status} />
              </Group>
            </Group>
            {c.duplicateOf && (
              <Group justify="flex-end" mt={-4} mb={4}>
                <DuplicateOf c={c} nested />
              </Group>
            )}
            {c.flags.length > 0 && (
              <Group gap={4} mb={4} wrap="wrap">
                <CaseFlagBadges flags={c.flags.slice(0, 3)} />
              </Group>
            )}
            {showClient && (
              <>
                <Text size="sm" fw={600}>
                  {c.clientName}
                </Text>
                <Text size="xs" c="dimmed" mb={6}>
                  {c.siteAddress}
                </Text>
              </>
            )}
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
              <div style={{ minWidth: 0, flex: 1 }}>
                <Text size="xs" c="dimmed" mb={2}>
                  w etapie {formatDuration(Date.now() - Date.parse(c.statusSince))}
                </Text>
                <CaseWork c={c} nested max={2} />
              </div>
              <div style={{ flexShrink: 0 }}>
                <Scopes c={c} />
              </div>
            </Group>
          </Link>
        ))}
      </Stack>
    </div>
  );
}

export function CasesPage({ me }: { me: MeDto }) {
  const [q, setQ] = useState('');
  const [debouncedQ] = useDebouncedValue(q, 300);
  const [view, setView] = useState<View>('aktywne');
  const [stages, setStages] = useState<string[]>([]);
  const [flag, setFlag] = useState<string | null>(null);
  const [trade, setTrade] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [mine, setMine] = useState(false);
  const [page, setPage] = useState(1);
  const [filtersOpen, filters] = useDisclosure(false);
  const trades = useTrades();
  const companies = useCompanies();
  const users = useOfficeUsers();

  // wybrane etapy zawężają widok; bez wyboru — wszystkie etapy widoku
  const effective = stages.length ? stages : VIEW_STAGES[view];
  const params = new URLSearchParams({ limit: String(PAGE), offset: String((page - 1) * PAGE) });
  if (debouncedQ.trim()) params.set('q', debouncedQ.trim());
  if (effective.length < ALL_STAGES.length) params.set('status', effective.join(','));
  if (flag) params.set('flag', flag);
  if (trade) params.set('trade', trade);
  if (companyId) params.set('companyId', companyId);
  if (ownerId) params.set('ownerId', ownerId);
  if (mine) params.set('mine', 'true');

  // lista odświeża się sama: co minutę (gdy karta jest widoczna), po powrocie do okna i po sygnale na żywo z serwera (liveCounters)
  const list = useQuery({ queryKey: ['cases', params.toString()], queryFn: () => api<Page<CaseListItemDto>>(`/cases?${params}`), refetchInterval: LIST_REFRESH_MS, refetchOnWindowFocus: true });
  const countsQ = useQuery({ queryKey: ['cases', 'status-counts'], queryFn: () => api<{ statuses: Record<string, number>; flags: Record<string, number> }>('/cases/status-counts'), refetchInterval: LIST_REFRESH_MS, refetchOnWindowFocus: true });
  const counts = { data: countsQ.data?.statuses };
  const reset = () => setPage(1);
  const sum = (st: CaseStatus[]) => st.reduce((a, s) => a + (counts.data?.[s] ?? 0), 0);
  const activeFilters = [stages.length > 0, flag, trade, companyId, ownerId, mine].filter(Boolean).length;

  const extra = (
    <Group gap="sm" wrap="wrap" align="flex-end">
      <MultiSelect
        placeholder={stages.length ? undefined : 'Status (wszystkie w widoku)'}
        data={VIEW_STAGES[view].map((s) => ({ value: s, label: `${CASE_STATUSES[s]}${counts.data?.[s] ? ` (${counts.data[s]})` : ''}` }))}
        value={stages}
        onChange={(v) => {
          setStages(v);
          reset();
        }}
        clearable
        w={{ base: '100%', sm: 300 }}
      />
      <Select
        placeholder="Oczekiwania i flagi"
        clearable
        data={(Object.keys(CASE_FLAGS) as CaseFlag[]).map((f) => ({ value: f, label: `${CASE_FLAGS[f].label}${countsQ.data?.flags[f] ? ` (${countsQ.data.flags[f]})` : ''}` }))}
        value={flag}
        onChange={(v) => {
          setFlag(v);
          reset();
        }}
        w={{ base: '100%', sm: 240 }}
      />
      <Select
        placeholder="Branża"
        clearable
        data={(trades.data ?? []).map((t) => ({ value: t.code, label: t.name }))}
        value={trade}
        onChange={(v) => {
          setTrade(v);
          reset();
        }}
        w={{ base: '100%', sm: 200 }}
      />
      <Select
        placeholder="Firma"
        clearable
        data={(companies.data ?? []).map((c) => ({ value: c.id, label: c.shortName }))}
        value={companyId}
        onChange={(v) => {
          setCompanyId(v);
          reset();
        }}
        w={{ base: '100%', sm: 200 }}
      />
      <Select
        placeholder="Opiekun"
        clearable
        data={(users.data ?? []).map((u) => ({ value: u.id, label: u.fullName }))}
        value={ownerId}
        onChange={(v) => {
          setOwnerId(v);
          reset();
        }}
        w={{ base: '100%', sm: 200 }}
        disabled={mine}
      />
      <Switch
        label="Tylko moje"
        checked={mine}
        onChange={(e) => {
          setMine(e.currentTarget.checked);
          reset();
        }}
        mb={8}
      />
    </Group>
  );

  return (
    <>
      <PageHeader
        title="Sprawy"
        description="Każda inwestycja od zapytania do odbioru. Sprawa może obejmować kilka branż realizowanych przez różne firmy — firma jest przypisana do zakresu prac."
        actions={
          canQuickCase(me) && (
            <Button leftSection={<IconPlus size={16} />} onClick={openQuickCase}>
              Nowe zapytanie
            </Button>
          )
        }
      />
      <Group mb="sm" gap="sm" wrap="wrap">
        <FilterChips
          value={view}
          onChange={(v) => {
            setView(v as View);
            setStages([]);
            reset();
          }}
          options={[
            { value: 'aktywne', label: `Aktywne${counts.data ? ` (${sum(OPEN_STAGES)})` : ''}` },
            { value: 'zamkniete', label: `Zamknięte${counts.data ? ` (${sum(CLOSED_STAGES)})` : ''}` },
            { value: 'wszystkie', label: 'Wszystkie' },
          ]}
        />
        <TextInput
          placeholder="Szukaj: numer, klient, adres…"
          leftSection={<IconSearch size={16} />}
          value={q}
          onChange={(e) => {
            setQ(e.currentTarget.value);
            reset();
          }}
          w={{ base: '100%', sm: 280 }}
        />
        <Button variant={activeFilters ? 'light' : 'default'} leftSection={<IconAdjustmentsHorizontal size={16} />} onClick={filters.toggle} hiddenFrom="sm">
          Filtry{activeFilters ? ` (${activeFilters})` : ''}
        </Button>
      </Group>
      <Collapse expanded={filtersOpen} hiddenFrom="sm" mb="md">
        {extra}
      </Collapse>
      <div className="filters-desktop">
        <Group mb="md" visibleFrom="sm">
          {extra}
        </Group>
      </div>
      <Card padding={0}>
        <QueryState isLoading={list.isLoading} error={list.error}>
          {list.data?.items.length ? (
            <CaseRows items={list.data.items} />
          ) : (
            <EmptyState icon={<IconBriefcase size={24} />} title="Brak spraw spełniających kryteria" hint={canQuickCase(me) ? 'Zmień filtry albo wyszukiwanie. Nowe zapytanie dodasz przyciskiem „Nowe zapytanie”.' : 'Zmień filtry albo wyszukiwanie.'} />
          )}
          <Group justify="space-between" px="md" py="sm" style={{ borderTop: '1px solid var(--app-border)' }}>
            <Text size="sm" c="dimmed">
              Spraw: {list.data?.total ?? 0}
            </Text>
            <Pagination size="sm" total={Math.max(1, Math.ceil((list.data?.total ?? 0) / PAGE))} value={page} onChange={setPage} />
          </Group>
        </QueryState>
      </Card>
    </>
  );
}
